import type {
  CycleCountVarianceReport,
  CycleCountVarianceReportItem,
} from '../models/cycle-count-session.model';
import { formatVarianceReportDate } from './cycle-count-session.utils';

// Full column order. The two cost columns ($ Impact, Unit Cost) sit at indices
// 7 and 8; they are dropped together when the caller cannot view cost (WI 32156).
const CSV_COLUMNS = [
  'Sub-Loc',
  'Material',
  'Description',
  'Expected',
  'Actual',
  'Broken',
  'Variance',
  '$ Impact',
  'Unit Cost',
  'Last Counted',
  'Counted By',
  'Type',
  'Adjusted',
  'Original Actual',
  'Adjusted Actual',
  'Adjusted By',
  'Notes',
];
const COST_COLUMN_INDICES = [7, 8]; // $ Impact, Unit Cost

function dropCostColumns<T>(cells: readonly T[]): T[] {
  return cells.filter((_, idx) => !COST_COLUMN_INDICES.includes(idx));
}

/**
 * Builds the variance-report CSV (header + one row per item) as a single string.
 * Extracted from the session page component so that component stays under the
 * file-size gate; the formatting rules (NF placeholders, damaged/broken column,
 * 2-dp money, adjusted-audit columns, quoted free-text) are pure and unit-tested
 * here. `varianceTypeLabelOf` is injected by the caller because the human-readable
 * type label lives with the component's display helpers.
 *
 * WI 32156: when `includeCost` is false (the caller lacks cost-view permission),
 * the two cost columns ($ Impact, Unit Cost) are removed from the header AND every
 * row, so a hidden-cost export never carries cost. Defaults to true, so existing
 * callers are unchanged.
 */
export function buildVarianceReportCsv(
  report: CycleCountVarianceReport,
  varianceTypeLabelOf: (item: CycleCountVarianceReportItem) => string,
  includeCost = true
): string {
  const headerCells = includeCost ? CSV_COLUMNS : dropCostColumns(CSV_COLUMNS);
  const rows = report.items
    .map((i) => {
      const cells = [
        i.subLocationCode,
        i.materialId,
        `"${i.materialDescription ?? ''}"`,
        i.expectedQty,
        i.isNotFound ? 'NF' : (i.actualQty ?? ''),
        i.damagedQty ?? '',
        i.isNotFound ? '' : (i.variance ?? ''),
        i.extendedVarianceCost != null ? i.extendedVarianceCost.toFixed(2) : '',
        i.unitCost != null ? i.unitCost.toFixed(2) : '',
        // Quoted: the "month, day, year" format (#31383) contains a comma, which would
        // otherwise split this into two columns and misalign every following field.
        `"${formatVarianceReportDate(i.lastCountedDate)}"`,
        // #31393: who counted this item. Quoted so a name containing a comma (e.g. "Smith, Russ")
        // cannot split into two columns and misalign every field after it.
        `"${i.countedByName ?? ''}"`,
        varianceTypeLabelOf(i),
        i.isAdjusted ? 'Yes' : 'No',
        i.isAdjusted ? (i.originalActualQty ?? 'NF') : '',
        i.isAdjusted ? (i.adjustedActualQty ?? '') : '',
        i.isAdjusted ? (i.adjustedBy ?? '') : '',
        `"${i.notes ?? ''}"`,
      ];
      return (includeCost ? cells : dropCostColumns(cells)).join(',');
    })
    .join('\n');
  return `${headerCells.join(',')}\n${rows}`;
}
